export interface SerializedError {
  [key: string]: unknown
  type: string
  message: string
  stack?: string
  cause?: unknown
}

export const RESERVED_KEYS: ReadonlySet<string> = new Set([
  'level',
  'severity',
  'time',
  'message',
  'logger',
  'service',
  'env',
  'version',
])

export const SENSITIVE_KEYS: readonly string[] = [
  'authorization',
  'cookie',
  'admin-api-secret-key',
  'x-e2e-turnstile-key',
  'x-telegram-bot-api-secret-token',
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'access_token',
  'refresh_token',
  'secret',
  'apiKey',
]

const IDENTIFIER = /^[A-Za-z_$][\w$]*$/

// pino's redact paths need bracket notation for keys that are not valid identifiers
function accessor(key: string) {
  return IDENTIFIER.test(key) ? `.${key}` : `["${key}"]`
}

export const REDACT_PATHS: readonly string[] = SENSITIVE_KEYS.flatMap((key) => [
  IDENTIFIER.test(key) ? key : `["${key}"]`,
  `*${accessor(key)}`,
  `*.*${accessor(key)}`,
  `req.headers${accessor(key)}`,
])

const OWN_ERROR_KEYS: ReadonlySet<string> = new Set(['type', 'message', 'stack', 'cause'])

const MAX_OWN_PROPERTIES = 20

const MAX_AGGREGATE_MEMBERS = 5

function primitive(value: unknown): { keep: boolean; value?: unknown } {
  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return { keep: true, value }
  }
  if (typeof value === 'bigint') {
    return { keep: true, value: value.toString() }
  }
  return { keep: false }
}

// driver errors carry what matters in own properties (pg `code`/`detail`, HTTP `status`); objects are left
// out so a request or a connection never ends up on the line
function copyOwnProperties(err: Error, serialized: SerializedError): void {
  let copied = 0
  for (const key of Object.keys(err)) {
    if (copied >= MAX_OWN_PROPERTIES) {
      return
    }
    if (OWN_ERROR_KEYS.has(key)) {
      continue
    }
    try {
      const result = primitive(Reflect.get(err, key))
      if (result.keep) {
        serialized[key] = result.value
        copied += 1
      }
    } catch {
      // a throwing getter is skipped
    }
  }
}

function describeNonError(err: unknown): string {
  try {
    return String(err)
  } catch {
    return '[unserializable]'
  }
}

export function serializeError(err: unknown, seen: Set<unknown> = new Set()): SerializedError | undefined {
  if (err === undefined || err === null) {
    return undefined
  }
  if (!(err instanceof Error)) {
    return { type: 'NonError', message: describeNonError(err) }
  }
  seen.add(err)
  const serialized: SerializedError = { type: err.name, message: err.message, stack: err.stack }
  try {
    copyOwnProperties(err, serialized)
  } catch {
    // a proxy that throws on key listing keeps only the basics
  }
  if (err.cause !== undefined) {
    serialized.cause = err.cause instanceof Error && !seen.has(err.cause) ? serializeError(err.cause, seen) : err.cause
  }
  // AggregateError (Promise.any, some drivers) keeps the real failures in errors, which the primitive copy skips
  const members: unknown = Reflect.get(err, 'errors')
  if (Array.isArray(members)) {
    serialized['errors'] = members
      .slice(0, MAX_AGGREGATE_MEMBERS)
      .map((member: unknown) => (seen.has(member) ? '[Circular]' : serializeError(member, seen)))
  }
  return serialized
}

// BigInt and circular references would otherwise make JSON.stringify throw
export function safeJsonReplacer(): (key: string, value: unknown) => unknown {
  const ancestors: unknown[] = []
  return function (this: unknown, _key: string, value: unknown) {
    if (typeof value === 'bigint') {
      return value.toString()
    }
    if (typeof value !== 'object' || value === null) {
      return value
    }
    while (ancestors.length > 0 && ancestors.at(-1) !== this) {
      ancestors.pop()
    }
    if (ancestors.includes(value)) {
      return '[Circular]'
    }
    ancestors.push(value)
    return value
  }
}
